/**
 * The basemap, and what to draw when it cannot be reached.
 *
 * OpenStreetMap through OpenFreeMap, which needs no key and no quota, with the
 * `pmtiles` protocol registered so pointing `VITE_BASEMAP_STYLE` at a
 * self-hosted archive is a swap of one variable. With no network at all there
 * is still a map: the rasters, the dots and the panel are all local, so the
 * product degrades to a data map on a plain ground rather than to nothing.
 *
 * The worker line is not optional and its failure is silent -- see
 * `docs/frontend.md`. MapLibre v6 finds its worker by resolving a relative URL
 * against its own module URL, which is wrong the moment a bundler inlines the
 * library into an application chunk: nothing throws, the map keeps its style,
 * its transform and its mouse events, and it never paints.
 */

import { Map as MapLibreMap, NavigationControl, addProtocol, setWorkerUrl } from "maplibre-gl";
import type { MapOptions } from "maplibre-gl";
// `?worker&url`: build the worker as its own entry, with its imports resolved
// into it, and hand back the URL. A plain `?url` works in dev and ships blank.
import workerUrl from "maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url";
import { Protocol } from "pmtiles";

import type { Manifest } from "./manifest.ts";

type StyleSpecification = Exclude<NonNullable<MapOptions["style"]>, string>;

const DEFAULT_STYLE = "https://tiles.openfreemap.org/styles/positron";
const PAGE = "#f9f9f7";

export type BasemapSource = "osm" | "offline";

export interface CreatedMap {
  map: MapLibreMap;
  source: BasemapSource;
  detail: string;
}

let configured = false;

function configure(): void {
  if (configured) return;
  setWorkerUrl(workerUrl);
  addProtocol("pmtiles", new Protocol().tile);
  configured = true;
}

function offlineStyle(): StyleSpecification {
  return {
    version: 8,
    sources: {},
    layers: [{ id: "background", type: "background", paint: { "background-color": PAGE } }],
  };
}

/**
 * MapLibre resolves a relative `glyphs` against the page and refuses a relative
 * `sprite` outright -- "Invalid sprite URL, must be absolute" -- so the one
 * root-relative URL in our own style has to be made absolute before the style
 * is handed over. It is done here rather than in the style we bake because a
 * baked absolute URL would name one host, and the whole point of the file is
 * that the same bytes serve the dev server and the real site.
 *
 * The failure it prevents is quiet: the sprite throws during `_load`, the map
 * paints roads and labels perfectly well, and only the icons are missing.
 */
function absoluteSprite(style: StyleSpecification): StyleSpecification {
  const { sprite } = style;
  if (typeof sprite !== "string" || /^[a-z]+:/i.test(sprite)) return style;
  return { ...style, sprite: new URL(sprite, location.href).href };
}

/**
 * The style is fetched here rather than handed to MapLibre as a URL so that a
 * failure is a decision -- draw the data on a plain ground, and say so -- rather
 * than a blank canvas and a console error.
 */
async function resolveStyle(): Promise<{ style: StyleSpecification; source: BasemapSource;
                                         detail: string }> {
  const url = (import.meta.env["VITE_BASEMAP_STYLE"] as string | undefined) ?? DEFAULT_STYLE;
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    // Resolved against the page, because the style we serve ourselves is named
    // by a root-relative path -- one `/basemap/style.json` that means the dev
    // server on :5174 and the real host without being rewritten between them.
    // `new URL` on its own throws on anything but an absolute URL, and the
    // throw lands in the catch below, so the map silently drew itself on a
    // plain ground with a perfectly good basemap sitting next to it.
    const style = absoluteSprite((await res.json()) as StyleSpecification);
    return { style, source: "osm", detail: new URL(url, location.href).host };
  } catch (err) {
    console.warn(`propmap: basemap ${url} unavailable, drawing on a plain ground:`, err);
    return { style: offlineStyle(), source: "offline", detail: `${url} unreachable` };
  }
}

export async function createMap(container: HTMLElement, manifest: Manifest,
                                zoomControl: boolean): Promise<CreatedMap> {
  configure();
  const { style, source, detail } = await resolveStyle();
  const [lon0, lat0, lon1, lat1] = manifest.bounds_wgs84;

  const map = new MapLibreMap({
    container,
    style,
    bounds: [[lon0, lat0], [lon1, lat1]],
    fitBoundsOptions: { padding: 0, animate: false },
    attributionControl: { compact: true },
    // the layers have no detail below their own cell size, so there is no
    // honest picture past the point where a cell fills the screen
    maxZoom: 17.5,
    minZoom: 8.5,
    maxPitch: 0,
    dragRotate: false,
    hash: false,
  });
  map.touchZoomRotate.disableRotation();
  if (zoomControl) {
    map.addControl(new NavigationControl({ showCompass: false, visualizePitch: false }),
                   "bottom-right");
  }
  await new Promise<void>((resolve) => {
    if (map.loaded()) return resolve();
    map.once("load", () => resolve());
  });
  return { map, source, detail };
}
